import * as React from "react";
import { cn } from "../lib/utils";

/**
 * Generate smooth SVG path from points using Catmull-Rom to Cubic Bezier conversion
 */
function getSmoothPath(points) {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let d = `M ${points[0].x} ${points[0].y}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = i > 0 ? points[i - 1] : points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = i < points.length - 2 ? points[i + 2] : p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;

    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }

  return d;
}

const Sparkline = React.forwardRef(
  (
    {
      data = [],
      variant = "area",
      color = "var(--color-success, #10b981)",
      height = 32,
      width = "100%",
      strokeWidth = 2,
      showGradient = true,
      showDot = true,
      interactive = false,
      onActiveChange,
      className,
      ...props
    },
    ref
  ) => {
    const gradientId = React.useId();
    const svgRef = React.useRef(null);
    const [activeIndex, setActiveIndex] = React.useState(null);

    // Normalize data to array of numbers and metadata
    const parsedData = React.useMemo(() => {
      if (!Array.isArray(data)) return [];
      return data
        .map((d, index) => {
          if (typeof d === "number") {
            return { value: d, label: undefined, index };
          }
          if (d && typeof d === "object" && typeof d.value === "number") {
            return { value: d.value, label: d.label, timestamp: d.timestamp, index };
          }
          return null;
        })
        .filter(Boolean);
    }, [data]);

    // Dimensions
    const svgWidth = 100;
    const svgHeight = typeof height === "number" ? height : 32;
    const padding = 3;

    // Coordinate calculation with zero-variance protection
    const { points, linePath, areaPath, lastPoint, activePoint } = React.useMemo(() => {
      if (parsedData.length === 0) {
        return { points: [], linePath: "", areaPath: "", lastPoint: null, activePoint: null };
      }

      const values = parsedData.map((p) => p.value);
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      const valRange = maxVal - minVal;

      const innerHeight = svgHeight - padding * 2;
      const count = parsedData.length;

      const calculatedPoints = parsedData.map((p, i) => {
        const x = count <= 1 ? svgWidth / 2 : (i / (count - 1)) * svgWidth;
        // If zero range (e.g. flat line [5, 5, 5]), render at midpoint
        const y =
          valRange === 0
            ? svgHeight / 2
            : svgHeight - padding - ((p.value - minVal) / valRange) * innerHeight;
        return { x, y, raw: p };
      });

      const smoothLine = getSmoothPath(calculatedPoints);
      const last = calculatedPoints[calculatedPoints.length - 1];
      const active = activeIndex !== null ? calculatedPoints[activeIndex] : null;

      let smoothArea = "";
      if (variant === "area" && calculatedPoints.length > 0) {
        const first = calculatedPoints[0];
        smoothArea = `${smoothLine} L ${last.x} ${svgHeight} L ${first.x} ${svgHeight} Z`;
      }

      return {
        points: calculatedPoints,
        linePath: smoothLine,
        areaPath: smoothArea,
        lastPoint: last,
        activePoint: active,
      };
    }, [parsedData, svgHeight, padding, variant, activeIndex]);

    const handleMouseMove = React.useCallback(
      (e) => {
        if (!interactive || points.length === 0 || !svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const clientX = e.clientX - rect.left;
        const normalizedX = (clientX / rect.width) * svgWidth;

        // Find nearest point
        let nearestIdx = 0;
        let minDist = Infinity;
        points.forEach((p, idx) => {
          const dist = Math.abs(p.x - normalizedX);
          if (dist < minDist) {
            minDist = dist;
            nearestIdx = idx;
          }
        });

        setActiveIndex(nearestIdx);
        onActiveChange?.(points[nearestIdx].raw);
      },
      [interactive, points, svgWidth, onActiveChange]
    );

    const handleMouseLeave = React.useCallback(() => {
      if (!interactive) return;
      setActiveIndex(null);
      onActiveChange?.(null);
    }, [interactive, onActiveChange]);

    if (parsedData.length === 0) {
      return (
        <div
          ref={ref}
          className={cn("inline-flex items-center justify-center text-xs text-muted-foreground", className)}
          style={{ height, width }}
          {...props}
        />
      );
    }

    return (
      <div
        ref={ref}
        className={cn("relative inline-block overflow-hidden select-none", className)}
        style={{ height, width }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          ref={svgRef}
          data-testid="sparkline-svg"
          aria-label="Sparkline trend"
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="none"
          className="size-full overflow-visible"
        >
          <defs>
            <linearGradient id={`sparkline-grad-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Area Fill */}
          {variant === "area" && showGradient && areaPath && (
            <path
              d={areaPath}
              fill={`url(#sparkline-grad-${gradientId})`}
              className="transition-all duration-300"
            />
          )}

          {/* Stroke Line */}
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-all duration-300"
          />

          {/* Endpoint Dot */}
          {showDot && lastPoint && activeIndex === null && (
            <circle
              cx={lastPoint.x}
              cy={lastPoint.y}
              r={strokeWidth * 1.5}
              fill={color}
              className="drop-shadow-sm"
            />
          )}

          {/* Active Hover Dot & Vertical Cursor */}
          {interactive && activePoint && (
            <g>
              <line
                x1={activePoint.x}
                y1={0}
                x2={activePoint.x}
                y2={svgHeight}
                stroke={color}
                strokeWidth={1}
                strokeDasharray="2,2"
                opacity={0.6}
              />
              <circle
                cx={activePoint.x}
                cy={activePoint.y}
                r={strokeWidth * 2}
                fill={color}
                stroke="var(--color-background, #141312)"
                strokeWidth={1.5}
              />
            </g>
          )}
        </svg>
      </div>
    );
  }
);
Sparkline.displayName = "Sparkline";

export { Sparkline };
